import styled, { ThemeProvider } from 'styled-components';
import { useTranslation } from 'react-i18next';

import { useSiteMetadataQuery } from '@klaviyo-mkt/gatsby-theme-core/src/hooks/useSiteMetadataQuery';
import { useHelpCenterCTAData } from '@klaviyo-mkt/gatsby-theme-core/src/hooks/useHelpCenterCTAData';
import type { IGlobalLayout } from '@klaviyo-mkt/gatsby-theme-core/src/types';

import { useUniversalNavigationQuery } from '@klaviyo-mkt/gatsby-source-universal-navigation/src/hooks/useUniversalNavigationQuery';
import type { IUniversalMenuData, IUniversalMenuItemWithCustomFields } from '@klaviyo-mkt/gatsby-source-universal-navigation/src/types';

import { convertLocaleForIconName } from '@klaviyo-mkt/gatsby-components/atoms/icon/utils';
import type { TIconName } from '@klaviyo-mkt/gatsby-components/atoms/icon/types';
import { Breadcrumbs } from '@klaviyo-mkt/gatsby-components/molecules/breadcrumbs';
import { Header } from '@klaviyo-mkt/gatsby-components/organisms/header';
import { SubNav } from '@klaviyo-mkt/gatsby-components/organisms/subnav';
import type { ISubNavItem } from '@klaviyo-mkt/gatsby-components/organisms/subnav/types';
import KlaviyoStatusIndicator from '@klaviyo-mkt/gatsby-components/organisms/klaviyo-status-indicator';
import { UniversalFooter } from '@klaviyo-mkt/gatsby-components/organisms/universal-footer';
import GlobalStyle from '@klaviyo-mkt/gatsby-components/shared/styles/global';
import { default as defaultTheme } from '@klaviyo-mkt/gatsby-components/shared/styles/themes/default';
import { useAuthentication } from '@klaviyo-mkt/gatsby-components/context/authentication';
import { useScrollDirection } from '@klaviyo-mkt/gatsby-components/hooks/useScrollDirection';

import { useAlertBarQuery } from '../../hooks/useAlertBarQuery';
import { useBreadcrumbs, useShouldDisplayBreadcrumbs } from '../../hooks/useBreadcrumbs';
import { useSiteLanguagesQuery } from '../../hooks/useSiteLanguagesQuery';
import SiteNavigationElementSchema from './site-navigation-element-schema';
import { transformMenuItems } from './utils';

const SiteWrapper = styled.div``;

const Layout = ({
  breadcrumbsBackground,
  bodyClass = '',
  children,
  heroBackground,
  navigationColor,
  navigationStyle = 'default',
  path = '',
  scrollIndicator,
  shortTitle = '',
  showBreadcrumbs = true,
  showFooter = true,
  showHeader = true,
  subnavMenu,
  theme = defaultTheme,
  translations,
}: IGlobalLayout) => {

  const { authenticationState: { loggedIn } } = useAuthentication();
  const showSubnav = typeof window !== 'undefined' 
    ? new URLSearchParams(window.location.search).get('showSubnav')?.toLowerCase() === 'true'
    : false;

  const alertBarData = useAlertBarQuery();
  const siteMetadata = useSiteMetadataQuery();
  /* @ts-ignore: this is valid, I can't narrow the return type */
  const { copyright, ctas, ctasLoggedIn, footer, header, b2cHeader, helpCenter: helpCenterHeaderData, utility }: IUniversalMenuData = useUniversalNavigationQuery({ multiLocale: false }) || {};

  /* get the help center header data if we need it */
  const helpCenterCTAData = useHelpCenterCTAData();
  const { sites } = useSiteLanguagesQuery();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const crumbs = useBreadcrumbs(path, shortTitle, locale);
  const breadcrumbColor = (heroBackground === 'lightSand' || breadcrumbsBackground === 'lightSand' || heroBackground === undefined) ? 'light' : 'dark';
  const { shouldShowBreadcrumbs } = useShouldDisplayBreadcrumbs(path, navigationStyle, showBreadcrumbs, locale);
  const headerToUse = path.includes('grow2') ? b2cHeader : header;

  // Transform the subnavMenu items if they exist
  const transformedSubnavMenu = subnavMenu ? {
    ...subnavMenu,
    items: transformMenuItems(subnavMenu.items),
  } : null;

  const shouldShowSubnav = showSubnav && transformedSubnavMenu?.items && transformedSubnavMenu.items.length > 0;

  return (
    <ThemeProvider theme={theme}>
      <SiteWrapper className={bodyClass}>
        <GlobalStyle />
        {(path === '/') && <SiteNavigationElementSchema />}
        {showHeader && (
          <>
            <Header
              alertBar={alertBarData}
              desktopLogoIconName={navigationStyle === 'power_up' ? `KlaviyoPowerUpLogo${convertLocaleForIconName(locale)}` as TIconName : 'Logo'}
              homeLink={siteMetadata?.pathPrefix ? `${siteMetadata?.pathPrefix}/` : '/'}
              navigationBackgroundColor={navigationColor}
              navigationStyle={navigationStyle}
              path={path}
              scrollIndicator={scrollIndicator}
              siteMetadata={siteMetadata}
              utilityChildren={navigationStyle === 'power_up' ? <KlaviyoStatusIndicator /> : null}
              universalCtas={navigationStyle === 'default' ? (loggedIn ? ctasLoggedIn : ctas) : helpCenterCTAData}
              universalNavigationItems={navigationStyle === 'default' ? headerToUse : helpCenterHeaderData}
              utilityNavigationItems={utility}
              showSubnav={shouldShowSubnav}
            />
            {shouldShowSubnav && (
              <SubNav
                menuItems={(transformedSubnavMenu?.items || []) as ISubNavItem[]}
                path={path}
              />
            )}
          </>
        )}
        <main id="site-content" role="main">
          {shouldShowBreadcrumbs && !shouldShowSubnav && <Breadcrumbs background={breadcrumbsBackground} breadcrumbs={crumbs} colorVariant={breadcrumbColor} />}
          {children}
        </main>
        {showFooter && (
          <UniversalFooter
            backgroundColor='cotton'
            menuItems={footer}
            copyrightMenuItems={copyright}
            navigationStyle={navigationStyle}
            siteMetadata={siteMetadata}
            sites={sites}
            translations={translations}
          />
        )}
      </SiteWrapper>
    </ThemeProvider>
  );
};

export default Layout;
